import { HomeOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import ProLayout, { PageContainer, BasicLayoutProps, MenuDataItem } from '@ant-design/pro-layout';
import { Route } from '@ant-design/pro-layout/lib/typings';
import {
  Breadcrumb,
  Row,
  Typography,
  Tooltip,
  Menu,
} from 'antd';
import classnames from 'classnames';
import { isEmpty } from 'lodash-es';
import React, {
  FC,
  ReactNode,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react';
import { Link, useLocation } from 'react-router-dom';
import './index.scss';

const { Link: TextLink } = Typography;

export interface TdsBasicLayoutProps extends BasicLayoutProps {
  leftMenuRoutes: Route;
  overlayMenuRoutes?: Route;
  appSelector?: ReactNode;
  topMenu?: ReactNode;
  sailingPlan?: ReactNode;
  liteModeRoutes?: string[];
  defaultOpenKeys?: string[];
  collapsed?: boolean;
  setCollapsed?: (isCollaPsed: boolean) => void;
  handlePath?: (path: string) => void;
  mainMenuLink?: string;
  renderBanner?: (
    showBanner: boolean,
    setShowBanner: React.Dispatch<React.SetStateAction<boolean>>,
  ) => ReactNode;
  renderArrearsBanner?: () => ReactNode;
  liteMode?: boolean;
  showBreadcrumb?: boolean;
  narrow?: boolean;
}

const BG_PATH_NAME = ['/setting/tag', '/setting/alert'];
let showOverlayTipsTimeout: NodeJS.Timeout | null = null;

export const BasicLayout: FC<TdsBasicLayoutProps> = (props) => {
  const {
    children,
    leftMenuRoutes,
    overlayMenuRoutes,
    logo,
    appSelector,
    topMenu,
    sailingPlan,
    liteMode = false,
    narrow = false,
    liteModeRoutes,
    title,
    className,
    collapsed,
    setCollapsed,
    handlePath,
    footerRender,
    mainMenuLink,
    renderBanner,
    renderArrearsBanner,
    showBreadcrumb = true,
    ...restProps
  } = props;
  const [showBanner, setShowBanner] = useState(false);
  const [showOverlayTips, setShowOverlayTips] = useState<boolean>();

  const flatten = (routes?: Route[], parent?: string) => {
    let flattenRoutes: Route[] = [];
    routes?.forEach((route) => {
      flattenRoutes.push({
        ...route,
        parent,
      });

      if (route.routes) {
        const subRoutes = flatten(route.routes, route.path);
        flattenRoutes = flattenRoutes.concat(subRoutes.map(subRoute => ({
          ...subRoute,
        })));
      }
    });
    return flattenRoutes;
  };

  // 打平后的路由配置，方便查找
  const flattenRoutes = React.useMemo(
    () => flatten([
      ...(leftMenuRoutes.routes || []),
      ...(overlayMenuRoutes?.routes || []),
    ]),
    [leftMenuRoutes, overlayMenuRoutes],
  );

  /**
   * 获取最后一次点击的菜单的openKeys，效果是只展开一个一级菜单，其他关闭
   * @param lastKey
   * @returns
   */
  const getLastOpenKeys = (lastKey: string) => {
    if (!lastKey) return [];
    let tempKey = lastKey;
    const lastOpenKeys: string[] = [lastKey];
    while (true) {
      // eslint-disable-next-line no-loop-func
      const currentRoute: any = flattenRoutes.find(route => route.path === tempKey);
      if (currentRoute?.parent) {
        tempKey = currentRoute.parent;
        lastOpenKeys.push(currentRoute.parent);
      } else {
        break;
      }
    }

    return lastOpenKeys;
  };

  const location = useLocation();

  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);

  useEffect(() => {
    const initRoute = flattenRoutes.find(route => route.path === location.pathname);
    const openKeys = getLastOpenKeys(initRoute?.parent);
    const selectedKeys = initRoute?.path ? [initRoute.path] : [];
    setOpenKeys(openKeys);
    setSelectedKeys(selectedKeys);
  }, [flattenRoutes]);

  const isLiteMode = () => liteMode || liteModeRoutes?.includes(location.pathname);

  const contentClassName = classnames({
    'page-container': true,
    'bugly-page-container': true,
    'bugly-page-container-lite': isLiteMode(),
    // 窄版
    'bugly-page-narrow': narrow,
  });

  const breadcrumb = (): ReactNode => {
    const routes: Route[] = [];
    let path = location.pathname;
    do {
      // eslint-disable-next-line no-loop-func
      const route = flattenRoutes.find(item => item.path === path);
      if (route) {
        routes.push(route);
        path = route.parent;
      } else {
        path = '';
      }
    } while (!!path);

    return (
      <div>
        {renderArrearsBanner?.()}
        <Breadcrumb
          className={classnames({
            breadcrumb: true,
            'narrow-mode': narrow,
          })}
        >
          <Breadcrumb.Item key="/">
            <Link to={mainMenuLink || '/'}>
              <HomeOutlined />
            </Link>
          </Breadcrumb.Item>
           {routes?.reverse()?.map((route: MenuDataItem) => (
            <Breadcrumb.Item key={route.name}>{route.name}</Breadcrumb.Item>
           ))}
        </Breadcrumb>
      </div>
    );
  };

  const headerContentRender = () => (
    <Row className="bugly-header-extra">
      <div>
        {isLiteMode() ? null : appSelector}
        {sailingPlan}
      </div>
      <div style={{
        textAlign: 'right',
        flex: 1,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
      }}>
        {topMenu}
      </div>
    </Row>
  );

  const isRouteMenuChange = (openKeys: string[]) => openKeys.some((openKey) => {
    const currentRoute: any = flattenRoutes.find(route => route.path === openKey);
    return !currentRoute?.parent;
  });

  const onOpenChange = (keys: string[]) => {
    if (isEmpty(keys) || !isRouteMenuChange(keys)) {
      return setOpenKeys(keys);
    }

    const lastKey = isEmpty(keys) ? '' : keys.pop();
    setOpenKeys(getLastOpenKeys(lastKey as string));
  };

  const onSelect = (e: { selectedKeys: string[] }) => {
    const { selectedKeys } = e;
    setSelectedKeys(selectedKeys);
  };

  const headerTitleRender = (logo: ReactNode) => <>{logo}</>;

  const menuItemRender = (item: MenuDataItem, dom: ReactNode) => {
    if (item.href) {
      return (
        <TextLink
          style={{
            paddingLeft: item.icon ? 0 : '10px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
          href={item.href}
          target={item.target}
        >
          {dom}
          {item.suffix}
        </TextLink>
      );
    }
    if (handlePath) {
      return (
        <div
          key={item.path}
          onClick={() => {
            item.onMenuClick?.();
            handlePath(item.path as string);
          }}
          style={{
            textDecoration: 'none',
            paddingLeft: item.icon ? 0 : '10px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          {dom}
          {item.suffix}
          {item?.tooltip ? <Tooltip
            title={item.tooltip?.tips}
            placement={item.tooltip?.placement || 'top'}
          >
            <QuestionCircleOutlined />
          </Tooltip> : null}
        </div>
      );
    }
    return (
      <Link
        key={item.path}
        style={{
          textDecoration: 'none',
          paddingLeft: item.icon ? 0 : '10px',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
        }}
        to={item.path as string}
      >
        {dom}
        {item.suffix}
      </Link>
    );
  };

  const subMenuItemRender = (item: MenuDataItem, dom: ReactNode) => (
    <div style={{ paddingLeft: item.flag === 'subMenu' ? '11px' : 0 }}>
      {dom}
    </div>
  );

  const renderOverlaysMenuItems = (routes?: Route[]) => {
    const renderMenuItem = (menus?: Route[]) => menus?.map((menu) => {
      const { path, icon, name, routes, hideInMenu } = menu;
      if (hideInMenu) {
        return null;
      }
      const key = path as string;

      if (routes?.length) {
        return (
          <Menu.SubMenu
            key={key}
            title={subMenuItemRender(menu, name)}
            icon={icon}
          >
            {renderMenuItem(routes)}
          </Menu.SubMenu>
        );
      }

      return (
        <Menu.Item
          key={key}
          icon={icon}
        >
          {menuItemRender(menu, name)}
        </Menu.Item>
      );
    });

    return renderMenuItem(routes);
  };

  const checkOverlayTips = () => {
    const menuDom = document.querySelector('.ant-layout-sider-children');
    const scrollDom = menuDom?.children?.[0];
    const contentDom = scrollDom?.children?.[0];
    const scrollHeigt = scrollDom?.clientHeight || 0;
    const contentHeigt = contentDom?.clientHeight || 0;
    if (scrollHeigt >= contentHeigt) {
      return;
    }
    setShowOverlayTips(true);
    setTimeout(() => setShowOverlayTips(false), 5000);
  };

  useLayoutEffect(() => {
    if (showOverlayTips !== undefined) {
      return;
    }
    if (showOverlayTipsTimeout) {
      clearTimeout(showOverlayTipsTimeout);
    }
    showOverlayTipsTimeout = setTimeout(checkOverlayTips, 1000);
  }, [leftMenuRoutes, overlayMenuRoutes]);

  useEffect(() => {
    if (showOverlayTips) {
      setShowOverlayTips(false);
    }
  }, [selectedKeys, openKeys]);

  const overlaysMenuRender = () => (collapsed ? (
    overlayMenuRoutes?.routes?.map(route => (
      <div className={'overlay-collapsed-icon'}>
        {route.icon}
      </div>
    ))) : [<>
    {showOverlayTips ? <Tooltip title={'向下滑动查看更多'} visible={true} trigger={'click'}>
      <div className={'overlay-tips'} />
    </Tooltip> : null}
    <Menu
      mode="inline"
      inlineIndent={16}
      className={'overlay-menu'}
      openKeys={openKeys}
      onOpenChange={onOpenChange}
      selectedKeys={selectedKeys}
      onSelect={onSelect}
    >
      {renderOverlaysMenuItems(overlayMenuRoutes?.routes)}
    </Menu>
  </>]);

  return (
    <ProLayout
      menuProps={{
        openKeys,
        onOpenChange,
        selectedKeys,
        onSelect,
      }}
      footerRender={footerRender}
      className={classnames(
        `${className} tds-layout`,
        showBanner ? 'show-banner' : '',
        BG_PATH_NAME.includes(location.pathname) ? 'tag-bg' : '',
        overlayMenuRoutes?.routes ? 'show-overlay' : '',
      )}
      route={leftMenuRoutes}
      title={title}
      logo={logo}
      location={location}
      navTheme="light"
      layout="mix"
      headerTheme="light"
      fixSiderbar
      fixedHeader
      collapsed={collapsed}
      onCollapse={setCollapsed}
      menu={{ defaultOpenAll: true }}
      headerTitleRender={headerTitleRender as any}
      headerContentRender={headerContentRender}
      menuRender={isLiteMode() ? false : undefined}
      menuItemRender={menuItemRender}
      subMenuItemRender={subMenuItemRender}
      links={overlaysMenuRender()}
      {...restProps}
    >
      {renderBanner?.(showBanner, setShowBanner)}
      <PageContainer
        className={contentClassName}
        header={{
          title: false,
          subTitle: false,
          breadcrumbRender: showBreadcrumb ? breadcrumb : undefined,
        }}
      >
        {children}
      </PageContainer>
    </ProLayout>
  );
};
